export type PublicDeploymentEnvironment = "local" | "preview" | "production";

const projectReferencePattern = /^[a-z0-9]{20}$/;
export const approvedVinteraProductionProjectReference = "cuhpgntimbthqzjscfjq";
export const approvedVinteraPartnerTrainingOrigin =
  "https://ax16pro.tail20f699.ts.net";

function deploymentEnvironment(value: string | undefined): PublicDeploymentEnvironment {
  const normalized = value?.trim().toLowerCase();
  if (normalized === "local" || normalized === "preview" || normalized === "production") {
    return normalized;
  }
  throw new Error(
    "NEXT_PUBLIC_VINTERA_DEPLOYMENT_ENV must explicitly identify local, preview, or production.",
  );
}

function productionProjectReference(value: string | undefined) {
  const normalized = value?.trim().toLowerCase() || "";
  if (normalized !== approvedVinteraProductionProjectReference) {
    throw new Error("NEXT_PUBLIC_VINTERA_PRODUCTION_SUPABASE_PROJECT_ID does not identify the approved Vintera project.");
  }
  return normalized;
}

function isLocalSupabaseHost(hostname: string) {
  const normalized = hostname.toLowerCase();
  return normalized === "localhost" || normalized === "127.0.0.1" || normalized === "::1" || normalized === "[::1]";
}

function isLoopbackApplicationOrigin(value: string | undefined) {
  if (!value) return false;

  try {
    const url = new URL(value);
    return (
      (url.protocol === "http:" || url.protocol === "https:")
      && isLocalSupabaseHost(url.hostname)
      && !url.username
      && !url.password
      && url.pathname === "/"
      && !url.search
      && !url.hash
    );
  } catch {
    return false;
  }
}

export function normalizeVinteraTrainingOrigin(value: string | undefined) {
  if (!value) return null;

  try {
    const url = new URL(value);
    const hostname = url.hostname.toLowerCase();
    if (
      url.protocol !== "https:"
      || Boolean(url.port)
      || url.username
      || url.password
      || (url.pathname !== "/" && url.pathname !== "")
      || url.search
      || url.hash
      || !/^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+\.ts\.net$/.test(hostname)
    ) {
      return null;
    }
    return `https://${hostname}`;
  } catch {
    return null;
  }
}

function assertOriginOnlyUrl(url: URL) {
  if (
    url.username
    || url.password
    || (url.pathname !== "/" && url.pathname !== "")
    || url.search
    || url.hash
  ) {
    throw new Error("NEXT_PUBLIC_SUPABASE_URL must be an origin without credentials, path, query, or fragment.");
  }
}

function localProductionProjectAccessAllowed(input: {
  applicationOrigin: string | undefined;
  currentReference: string;
  deploymentEnvironment: PublicDeploymentEnvironment;
  localProductionAccessOptIn: string | undefined;
  productionProjectReference: string;
  serverDeploymentEnvironment: string | undefined;
}) {
  return (
    input.deploymentEnvironment === "local"
    && input.serverDeploymentEnvironment?.trim().toLowerCase() === "local"
    && input.localProductionAccessOptIn?.trim().toLowerCase() === "true"
    && isLoopbackApplicationOrigin(input.applicationOrigin)
    && input.currentReference === approvedVinteraProductionProjectReference
    && input.productionProjectReference === approvedVinteraProductionProjectReference
  );
}

function localTrainingProjectAccessAllowed(input: {
  applicationOrigin: string | undefined;
  currentReference: string;
  deploymentEnvironment: PublicDeploymentEnvironment;
  localProductionAccessOptIn: string | undefined;
  productionProjectReference: string;
  serverDeploymentEnvironment: string | undefined;
  trainingAccessOptIn: string | undefined;
  trainingOrigin: string | undefined;
}) {
  const configuredTrainingOrigin = normalizeVinteraTrainingOrigin(input.trainingOrigin);
  let applicationOrigin: string | null = null;
  try {
    applicationOrigin = input.applicationOrigin
      ? new URL(input.applicationOrigin).origin.toLowerCase()
      : null;
  } catch {
    applicationOrigin = null;
  }

  return (
    input.deploymentEnvironment === "local"
    && input.serverDeploymentEnvironment?.trim().toLowerCase() === "local"
    && input.localProductionAccessOptIn?.trim().toLowerCase() === "true"
    && input.trainingAccessOptIn?.trim().toLowerCase() === "true"
    && configuredTrainingOrigin !== null
    && configuredTrainingOrigin === approvedVinteraPartnerTrainingOrigin
    && applicationOrigin === configuredTrainingOrigin
    && input.currentReference === approvedVinteraProductionProjectReference
    && input.productionProjectReference === approvedVinteraProductionProjectReference
  );
}

export function assertTrainingFunnelEnvironment(input: {
  deploymentEnvironment: string | undefined;
  localProductionAccessOptIn: string | undefined;
  productionProjectReference: string | undefined;
  publicDeploymentEnvironment: string | undefined;
  publicTrainingAccessOptIn: string | undefined;
  publicTrainingOrigin: string | undefined;
  serverTrainingAccessOptIn: string | undefined;
  serverTrainingOrigin: string | undefined;
}) {
  const serverEnabled = input.serverTrainingAccessOptIn?.trim().toLowerCase() === "true";
  const publicEnabled = input.publicTrainingAccessOptIn?.trim().toLowerCase() === "true";
  const anyTrainingConfiguration = serverEnabled
    || publicEnabled
    || Boolean(input.serverTrainingOrigin)
    || Boolean(input.publicTrainingOrigin);
  if (!anyTrainingConfiguration) return null;

  const serverOrigin = normalizeVinteraTrainingOrigin(input.serverTrainingOrigin);
  const publicOrigin = normalizeVinteraTrainingOrigin(input.publicTrainingOrigin);
  if (
    !serverEnabled
    || !publicEnabled
    || input.deploymentEnvironment?.trim().toLowerCase() !== "local"
    || input.publicDeploymentEnvironment?.trim().toLowerCase() !== "local"
    || input.localProductionAccessOptIn?.trim().toLowerCase() !== "true"
    || input.productionProjectReference?.trim().toLowerCase() !== approvedVinteraProductionProjectReference
    || !serverOrigin
    || serverOrigin !== approvedVinteraPartnerTrainingOrigin
    || serverOrigin !== publicOrigin
  ) {
    throw new Error("Temporary partner training requires matching exact local-only server and public Funnel configuration.");
  }
  return serverOrigin;
}

export function assertSupabaseDeploymentBoundary(input: {
  supabaseUrl: string;
  deploymentEnvironment: string | undefined;
  productionProjectReference: string | undefined;
  applicationOrigin?: string | undefined;
  localProductionAccessOptIn?: string | undefined;
  serverDeploymentEnvironment?: string | undefined;
  trainingAccessOptIn?: string | undefined;
  trainingOrigin?: string | undefined;
}) {
  let url: URL;
  try {
    url = new URL(input.supabaseUrl);
  } catch {
    throw new Error("NEXT_PUBLIC_SUPABASE_URL is invalid.");
  }

  assertOriginOnlyUrl(url);
  const environment = deploymentEnvironment(input.deploymentEnvironment);
  const serverEnvironment = deploymentEnvironment(input.serverDeploymentEnvironment);
  if (serverEnvironment !== environment) {
    throw new Error("Public and server Vintera deployment environments do not match.");
  }
  const productionReference = productionProjectReference(input.productionProjectReference);

  if (isLocalSupabaseHost(url.hostname)) {
    if (environment !== "local" || url.protocol !== "http:" || url.port !== "54321") {
      throw new Error("Only an explicitly local deployment may use the loopback Supabase endpoint on port 54321.");
    }
    return;
  }

  const [currentReference, ...domain] = url.hostname.toLowerCase().split(".");
  if (
    !projectReferencePattern.test(currentReference)
    || domain.join(".") !== "supabase.co"
    || url.protocol !== "https:"
    || Boolean(url.port)
  ) {
    throw new Error("Remote Supabase endpoint must be the expected HTTPS project endpoint.");
  }

  if (environment === "production") {
    if (currentReference !== approvedVinteraProductionProjectReference) {
      throw new Error("Production deployment does not reference the approved Vintera Supabase project.");
    }
    return;
  }

  if (environment === "preview") {
    if (currentReference === productionReference) {
      throw new Error("Preview deployment cannot use the production Supabase project.");
    }
    return;
  }

  if (
    currentReference === approvedVinteraProductionProjectReference
    && (
      localProductionProjectAccessAllowed({
        applicationOrigin: input.applicationOrigin,
        currentReference,
        deploymentEnvironment: environment,
        localProductionAccessOptIn: input.localProductionAccessOptIn,
        productionProjectReference: productionReference,
        serverDeploymentEnvironment: input.serverDeploymentEnvironment,
      })
      || localTrainingProjectAccessAllowed({
        applicationOrigin: input.applicationOrigin,
        currentReference,
        deploymentEnvironment: environment,
        localProductionAccessOptIn: input.localProductionAccessOptIn,
        productionProjectReference: productionReference,
        serverDeploymentEnvironment: input.serverDeploymentEnvironment,
        trainingAccessOptIn: input.trainingAccessOptIn,
        trainingOrigin: input.trainingOrigin,
      })
    )
  ) {
    return;
  }

  throw new Error("Local deployment may use only loopback Supabase or the exact explicitly authorized Vintera project.");
}

export function assertCurrentSupabaseDeploymentBoundary(supabaseUrl: string) {
  const isBrowser = typeof window !== "undefined";

  assertSupabaseDeploymentBoundary({
    supabaseUrl,
    deploymentEnvironment: process.env.NEXT_PUBLIC_VINTERA_DEPLOYMENT_ENV,
    productionProjectReference: process.env.NEXT_PUBLIC_VINTERA_PRODUCTION_SUPABASE_PROJECT_ID,
    applicationOrigin: isBrowser ? window.location.origin : process.env.APP_URL,
    localProductionAccessOptIn: isBrowser
      ? process.env.NEXT_PUBLIC_VINTERA_ALLOW_LOCAL_PRODUCTION_SUPABASE
      : process.env.VINTERA_ALLOW_LOCAL_PRODUCTION_SUPABASE,
    serverDeploymentEnvironment: isBrowser
      ? process.env.NEXT_PUBLIC_VINTERA_DEPLOYMENT_ENV
      : process.env.VINTERA_DEPLOYMENT_ENV,
    trainingAccessOptIn: isBrowser
      ? process.env.NEXT_PUBLIC_VINTERA_ALLOW_TRAINING_FUNNEL
      : process.env.VINTERA_ALLOW_TRAINING_FUNNEL,
    trainingOrigin: isBrowser
      ? process.env.NEXT_PUBLIC_VINTERA_LOCAL_TRAINING_ORIGIN
      : process.env.VINTERA_LOCAL_TRAINING_ORIGIN,
  });
}
